import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle, Mail } from 'lucide-react';
import { Button } from './button';
import { contactInfo } from '../../data/contact-info';
import logoSvg from '../../assets/Logo_310x150_trans.svg';
import { useScrollSpy } from '../../hooks/useScrollSpy';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(true);
  const activeSection = useScrollSpy();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Zeige Contact-Info nur wenn User oben ist (z.B. < 50px)
      setShowContactInfo(currentScrollY < 50);
    };

    // Initial check
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    const section = document.getElementById(sectionId);
    
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getNavItemClass = (sectionId: string) => {
    if (sectionId === 'services' && activeSection === 'showroom') {
      return `text-foreground transition-all duration-300 font-medium cursor-pointer text-primary scale-105 font-bold`;
    }
    
    const isActive = activeSection === sectionId;
    return `text-foreground transition-all duration-300 font-medium cursor-pointer
      ${isActive 
        ? 'text-primary scale-105 font-bold' 
        : 'hover:text-primary hover:scale-105'
      }`;
  };

  return (
    <header className="bg-white shadow-md fixed top-0 left-0 w-full z-50 py-4">
      <div className="container">
        <div className="flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex flex-col lg:flex-row lg:items-center">
            <a href="#" onClick={scrollToTop} className="flex items-center">
              <img 
                src={logoSvg} 
                alt="DIETRONIK Service Logo" 
                className="h-28"
              />
            </a>
            
            {/* Contact Info for Mobile */}
            <AnimatePresence>
              {showContactInfo && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="lg:hidden flex flex-col items-start gap-2 mt-2 px-2 text-sm text-muted-foreground overflow-hidden"
                >
                  <a 
                    href="https://wa.me/message/AUKCMKCVRAIYM1" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2 transition-all duration-300 hover:text-primary hover:scale-105"
                  >
                    <MessageCircle 
                      size={20} 
                      className="text-[#0EA5E9]" 
                      strokeWidth={2.5} 
                      fill="transparent" 
                    />
                    <span>{contactInfo.phone.mobile}</span>
                  </a>
                  
                  <a 
                    href={`mailto:${contactInfo.email}`} 
                    className="flex items-center gap-2 transition-all duration-300 hover:text-primary hover:scale-105"
                  >
                    <Mail size={20} className="text-[#0EA5E9]" />
                    <span>{contactInfo.email}</span>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Contact Info for Desktop */}
          <div className="hidden lg:flex gap-6 text-sm text-muted-foreground">
            <a 
              href="https://wa.me/message/AUKCMKCVRAIYM1" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-2 transition-all duration-300 hover:text-primary hover:scale-105"
            >
              <MessageCircle 
                size={24} 
                className="text-[#0EA5E9] transition-all duration-300" 
                strokeWidth={2.5} 
                fill="transparent" 
              />
              <span>{contactInfo.phone.mobile}</span>
            </a>
            <a 
              href={`mailto:${contactInfo.email}`} 
              className="flex items-center gap-2 transition-all duration-300 hover:text-primary hover:scale-105"
            >
              <Mail size={24} className="text-[#0EA5E9] transition-all duration-300" />
              <span>{contactInfo.email}</span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 text-sm">
            <button onClick={() => scrollToSection('home')} className={getNavItemClass('home')}>
              Home
            </button>
            <button onClick={() => scrollToSection('services')} className={getNavItemClass('services')}>
              Dienstleistungen
            </button>
            <button onClick={() => scrollToSection('about')} className={getNavItemClass('about')}>
              Über uns
            </button>
            <button onClick={() => scrollToSection('contact')} className={getNavItemClass('contact')}>
              Kontakt
            </button>
            <Button 
              variant="default" 
              onClick={() => window.open('https://www.dietronik-shop.de/39202120/startseite', '_blank', 'noopener,noreferrer')}
            >
              D-Shop
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(open => !open)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden mt-4 pb-4 space-y-4"
            >
              <div className="flex flex-col space-y-4">
                <button 
                  onClick={() => scrollToSection('home')} 
                  className="text-foreground hover:text-primary transition-all duration-300 hover:scale-105 px-2 py-1 text-left"
                >
                  Home
                </button>
                <button 
                  onClick={() => scrollToSection('services')} 
                  className="text-foreground hover:text-primary transition-all duration-300 hover:scale-105 px-2 py-1 text-left"
                >
                  Dienstleistungen
                </button>
                <button 
                  onClick={() => scrollToSection('about')} 
                  className="text-foreground hover:text-primary transition-all duration-300 hover:scale-105 px-2 py-1 text-left"
                >
                  Über uns
                </button>
                <button 
                  onClick={() => scrollToSection('contact')} 
                  className="text-foreground hover:text-primary transition-all duration-300 hover:scale-105 px-2 py-1 text-left"
                >
                  Kontakt
                </button>
                <Button 
                  variant="default" 
                  className="w-full" 
                  onClick={() => window.open('https://www.dietronik-shop.de/39202120/startseite', '_blank', 'noopener,noreferrer')}
                >
                  D-Shop
                </Button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Navigation; 